/**
 * Splash + boot states — the whole cold-load wait, designed to the
 * "Vault Splash & Loading" brief. The padlock is the only motion (a slow 2.4s
 * breathe, never a rotating spinner) and the accent bar fills once. The rule
 * the old build broke: the splash NEVER hangs — after 3 seconds it offers the
 * last synced month held on the device plus a retry, so a wait always has a
 * way out.
 *
 * Offline and failure are first-class screens, not dead ends. Offline names
 * the held month and says what still works (tick, log, add); a failure blames
 * the screen, not the month ("your data is safe and untouched — this is the
 * screen, not your month") and routes a repeat to the ? report.
 *
 * One asset, four geometries: the mark, word and track size from viewport
 * tokens (phone 82 / tablet 92 / big 108, track capped at 70%, stuck card
 * capped at 460px) so a phone-centred lockup never reads as a crash on a wide
 * screen. Nocturne: ground is --color-bg (ink), the bar and breathe are the
 * accent, the amber is --color-warn, no pure black.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useState } from 'react';

const AMBER = '#e0a75f';   // --color-warn — the "taking too long / waiting" tone
const dim = (pct) => `color-mix(in srgb, var(--color-text) ${pct}%, transparent)`;

/** Was a month ever synced onto this device? Gates "Open my last month". */
export function hasLastMonth() {
  try { return !!localStorage.getItem('vault_last_me'); } catch { return false; }
}
/** The human label of the last synced month, if the app cached one. */
export function lastMonthLabel() {
  try { return localStorage.getItem('vault_last_month') || null; } catch { return null; }
}
/** Count of local edits queued while offline (0 until the outbox exists). */
function queuedCount() {
  try { return (JSON.parse(localStorage.getItem('vault_outbox') || '[]') || []).length; }
  catch { return 0; }
}

// Size the single lockup per viewport — never four hand-built screens.
function geoFor(w) {
  if (w < 480) return { mark: 82, word: 27, track: 230, gap: 15, big: false };
  if (w < 900) return { mark: 92, word: 27, track: 230, gap: 15, big: false };
  return { mark: 108, word: 32, track: 300, gap: 20, big: true };
}
function useGeo() {
  const [g, setG] = useState(() => geoFor(typeof window !== 'undefined' ? window.innerWidth : 1240));
  useEffect(() => {
    const on = () => setG(geoFor(window.innerWidth));
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return g;
}

const SHELL = 'fixed inset-0 z-[200] flex flex-col items-center bg-ink text-slate-100';
const SECONDARY = 'border border-edge text-slate-200 font-semibold rounded-xl px-5 hover:bg-white/5 transition';

/**
 * @param view 'splash' (cold load; self-arms the 3s deadline) | 'offline' | 'error'
 * @param onRetry     re-attempt the boot / reconnect
 * @param onOpenLast  open the last synced month held on the device
 * @param hasLast     whether a last-synced month exists to open
 */
export default function Splash({ view = 'splash', onRetry, onOpenLast, hasLast = false }) {
  const g = useGeo();
  const [stuck, setStuck] = useState(view === 'stuck');
  const [phase, setPhase] = useState(0);   // three honest boot phases before the 3s deadline

  // THE DEADLINE: a spinner with no exit is forbidden. At 3s the splash offers
  // the way out. Only the cold splash arms it; offline/error are already exits.
  useEffect(() => {
    if (view !== 'splash') return undefined;
    // Three honest phases (open → load the month → finalise), then at 3s the
    // deadline: offer the last synced month. A spinner with no exit is forbidden.
    const t1 = setTimeout(() => setPhase(1), 1000);
    const t2 = setTimeout(() => setPhase(2), 2000);
    const t3 = setTimeout(() => setStuck(true), 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [view]);

  if (view === 'offline' || view === 'error') {
    return <BootMessage kind={view} onRetry={onRetry} onOpenLast={onOpenLast} hasLast={hasLast} g={g} />;
  }

  const monthLbl = lastMonthLabel();
  const PHASES = ['Opening your vault\u2026', `Loading ${monthLbl || 'your month'}\u2026`, 'Almost there\u2026'];
  const barW = stuck ? '84%' : ['42%', '64%', '84%'][phase];
  return (
    <div className={SHELL} style={{ padding: g.big ? '40px 60px' : '34px 28px' }} data-splash="cold">
      <div className="flex-1" />
      <div className="flex flex-col items-center" style={{ gap: g.gap }}>
        <img src="/logo.svg" alt="Vault" className="v-breathe block"
             style={{ width: g.mark, height: g.mark, borderRadius: g.big ? 28 : 22 }} />
        <div style={{ fontSize: g.word, fontWeight: 600, letterSpacing: '-.02em' }}>Vault</div>
        <div style={{ width: g.track, maxWidth: '70%' }}>
          <div style={{ height: 4, borderRadius: 2, background: 'var(--color-surface-2)', overflow: 'hidden', display: 'flex' }}>
            <div className={'v-growbar' + (stuck ? ' stuck' : '')}
                 style={{ height: '100%', background: 'var(--color-accent)', width: barW }} />
          </div>
          <div style={{ fontSize: 12.5, textAlign: 'center', marginTop: 11, color: dim(52) }}>
            {stuck ? 'Waiting on the network…' : PHASES[phase]}
          </div>
        </div>
      </div>
      <div className="flex-1 flex flex-col justify-end items-center w-full">
        {stuck && hasLast ? (
          <div style={{ width: '100%', maxWidth: 460, padding: 16, borderRadius: 'var(--radius)',
                        boxShadow: `inset 0 0 0 1px ${AMBER}`, background: 'color-mix(in srgb, #e0a75f 7%, transparent)',
                        animation: 'vFade .3s ease-out', marginBottom: 8 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: AMBER }}>Taking longer than it should</div>
            <div style={{ fontSize: 12.5, lineHeight: 1.55, marginTop: 4, color: dim(60) }}>
              {monthLbl
                ? `Your last synced month — ${monthLbl} — is on this device. Open it now and Vault catches up in the background.`
                : 'Your last synced month is on this device. Open it now and Vault catches up in the background.'}
            </div>
            <button className="btn-primary w-full" style={{ minHeight: 46, marginTop: 12 }} onClick={onOpenLast}>Open my last month</button>
            <button className="w-full text-slate-400 hover:text-slate-200 transition" style={{ minHeight: 40, marginTop: 5, fontSize: 12.5 }} onClick={onRetry}>Try again</button>
          </div>
        ) : stuck ? (
          <div style={{ width: '100%', maxWidth: 460, padding: 16, borderRadius: 'var(--radius)',
                        boxShadow: `inset 0 0 0 1px ${AMBER}`, background: 'color-mix(in srgb, #e0a75f 7%, transparent)',
                        animation: 'vFade .3s ease-out', marginBottom: 8 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: AMBER }}>Taking longer than it should</div>
            <div style={{ fontSize: 12.5, lineHeight: 1.55, marginTop: 4, color: dim(60) }}>
              We can't reach the network right now. Your sign-in is safe — try again in a moment.
            </div>
            <button className="btn-primary w-full" style={{ minHeight: 46, marginTop: 12 }} onClick={onRetry}>Try again</button>
          </div>
        ) : (
          <div style={{ fontSize: 11.5, paddingBottom: 6, color: dim(28) }}>vault-app.co.za</div>
        )}
      </div>
    </div>
  );
}

/** Offline and failure — first-class message screens, never dead ends. */
function BootMessage({ kind, onRetry, onOpenLast, hasLast, g }) {
  const offline = kind === 'offline';
  const monthLbl = lastMonthLabel();
  const queued = queuedCount();
  return (
    <div className={SHELL + ' justify-center'} style={{ padding: g.big ? '40px 60px' : '34px 26px', textAlign: 'center' }}
         data-splash={kind}>
      <div className="flex flex-col items-center" style={{ gap: 13, maxWidth: 440 }}>
        <div style={{ width: g.big ? 64 : 54, height: g.big ? 64 : 54, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: g.big ? 26 : 22,
                      background: 'color-mix(in srgb, var(--color-accent) 16%, transparent)',
                      boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--color-accent) 42%, transparent)',
                      color: 'var(--color-accent-400)' }}>
          {offline ? '☁' : '⚠'}
        </div>
        <div style={{ fontSize: g.big ? 26 : 22, fontWeight: 600, letterSpacing: '-.015em' }}>
          {offline ? "You're offline" : "That didn't load"}
        </div>
        <div style={{ fontSize: 13.5, lineHeight: 1.65, color: dim(58) }}>
          {offline
            ? `Showing your last synced month${monthLbl ? ` — ${monthLbl}` : ''}. You can still tick rows, log spends and add expenses; everything queues and syncs the moment you're back on signal.`
            : 'Something on our side did not answer. Your data is safe and untouched — this is the screen, not your month. Your last synced copy is still on this device.'}
        </div>
        <div className="flex gap-2 flex-wrap justify-center" style={{ marginTop: 6 }}>
          {offline ? (
            <>
              <button className="btn-primary" style={{ minHeight: 46 }} onClick={onOpenLast} disabled={!hasLast}>Carry on offline</button>
              <button className={SECONDARY} style={{ minHeight: 46 }} onClick={onRetry}>Try to reconnect</button>
            </>
          ) : (
            <>
              <button className="btn-primary" style={{ minHeight: 46 }} onClick={onRetry}>Try again</button>
              <button className={SECONDARY} style={{ minHeight: 46 }} onClick={onOpenLast} disabled={!hasLast}>Open my last month</button>
            </>
          )}
        </div>
        {offline ? (
          queued > 0 ? (
            <div className="flex items-center gap-2" style={{ marginTop: 10, padding: '8px 13px', borderRadius: 999, boxShadow: 'inset 0 0 0 1px var(--color-divider)' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: AMBER }} />
              <span style={{ fontSize: 11.5, color: dim(50) }}>{queued} {queued === 1 ? 'change' : 'changes'} waiting to sync</span>
            </div>
          ) : (
            <div style={{ fontSize: 11.5, marginTop: 8, color: dim(34) }}>Your last synced copy is on this device.</div>
          )
        ) : (
          <div style={{ fontSize: 11.5, lineHeight: 1.6, marginTop: 8, color: dim(34) }}>
            If it keeps happening, the ? button files it to us with the details attached.
          </div>
        )}
      </div>
    </div>
  );
}
